'use client';

import { __assign } from '../_virtual/_tslib.js';
import React__default, { useState } from 'react';
import { Path, PieChartWrapper, GroupCircle, LineDetail, Legend, LegendLabel, PercentView, CenterText, PieChartToolTip, PieChartToolTipValue, OneCircle } from './PieChart.css.js';
import { DEFAULT_OPTIONS, DEFAULT_MAX_RADIUS, DEFAULT_COLORS } from './constants.js';
import { getListLevel, mapRadiusIndex, findOnlyOneValue } from './helper.js';
import { ConvertHexToRgbA } from '../Utils/helpers.js';
import { TooltipText, TooltipDot } from '../BarChart/BarChart.css.js';

var PieChart = function (props) {
    var value = props.value, options = props.options;
    var mergedOptions = __assign(__assign({}, DEFAULT_OPTIONS), options);
    var _a = useState(-1), currentHoverIndex = _a[0], setCurrentHoverIndex = _a[1];
    var _b = useState(false), isHover = _b[0], setIsHover = _b[1];
    // First sort percent by large to small
    var sortLargest = getListLevel(value);
    // re-mapped values
    var values = mapRadiusIndex(value, sortLargest, mergedOptions.highlightColor);
    var _c = findOnlyOneValue(values), isHaveOneValue = _c.isHaveOneValue, onlyValue = _c.onlyValue, onlyValueIndex = _c.onlyValueIndex;
    var valuesInNumber = values.map(function (item) { return item.percent; });
    var colors = values.map(function (item) { return item.color; });
    var radii = values.map(function (item) { return item.minusRadius; });
    var total = valuesInNumber.reduce(function (acc, curr) { return acc + curr; }, 0);
    var angles = valuesInNumber.map(function (value) { return (value / total) * 360; });
    var getFillColor = function (color, isFullAlpha) {
        var opacity = isFullAlpha ? '1' : '0.2';
        if (!isHover) {
            opacity = '1';
        }
        if (!mergedOptions.isFocusHover) {
            opacity = '1';
        }
        var rgba = ConvertHexToRgbA(color, opacity);
        return rgba;
    };
    var segments = angles.map(function (angle, index) {
        var _a, _b;
        var prevAngles = angles.slice(0, index);
        var startAngle = prevAngles.reduce(function (acc, curr) { return acc + curr; }, 0);
        var endAngle = startAngle + angle;
        var largeArcFlag = angle > 180 ? 1 : 0;
        var radius = mergedOptions.isUsingRadiusRange ? radii[index] : DEFAULT_MAX_RADIUS;
        var startX = 50 + radius * Math.cos(((startAngle - 90) * Math.PI) / 180);
        var startY = 50 + radius * Math.sin(((startAngle - 90) * Math.PI) / 180);
        var endX = 50 + radius * Math.cos(((endAngle - 90) * Math.PI) / 180);
        var endY = 50 + radius * Math.sin(((endAngle - 90) * Math.PI) / 180);
        var pathData = "M50,50 L".concat(startX, ",").concat(startY, " A").concat(radius, ",").concat(radius, " 0 ").concat(largeArcFlag, ",1 ").concat(endX, ",").concat(endY, " Z");
        if (angle === 0) {
            return null;
        }
        var pieAnimationDuration = "".concat((index + 1) * 0.2, "s");
        return (React__default.createElement(Path, { key: index, d: pathData, fill: getFillColor((_a = colors[index]) !== null && _a !== void 0 ? _a : DEFAULT_COLORS[0], currentHoverIndex === index), stroke: '#fff', strokeWidth: (_b = mergedOptions.pieStrokeWidth) !== null && _b !== void 0 ? _b : 1, onMouseEnter: function () {
                setCurrentHoverIndex(index);
                setIsHover(true);
            }, style: {
                animationDuration: pieAnimationDuration
            }, onClick: function () {
                setCurrentHoverIndex(index);
                setIsHover(true);
            } }));
    });
    var renderCenterText = function () {
        var _a;
        if (isHaveOneValue) {
            return (React__default.createElement(CenterText, { x: 50, y: 55, textAnchor: 'middle' },
                onlyValue.percent,
                "%"));
        }
        if (!isHover) {
            return null;
        }
        if (currentHoverIndex < 0) {
            return null;
        }
        var itemValue = (_a = valuesInNumber[currentHoverIndex]) !== null && _a !== void 0 ? _a : 0;
        return (React__default.createElement(CenterText, { x: 50, y: 55, textAnchor: 'middle' },
            itemValue,
            "%"));
    };
    var renderHover = function () {
        var _a, _b;
        if (!isHover) {
            return null;
        }
        if (currentHoverIndex < 0) {
            return null;
        }
        var item = (_a = values[currentHoverIndex]) !== null && _a !== void 0 ? _a : null;
        if (item === null) {
            return null;
        }
        return (React__default.createElement(PieChartToolTip, null,
            React__default.createElement(TooltipDot, { bgColor: (_b = item.color) !== null && _b !== void 0 ? _b : DEFAULT_COLORS[0] }),
            " ",
            item.label,
            ":",
            ' ',
            React__default.createElement(PieChartToolTipValue, { highlightColor: mergedOptions.highlightColor },
                item.percent,
                "%"),
            item.isActive && (React__default.createElement(React__default.Fragment, null,
                ' ',
                React__default.createElement(TooltipText, { isMarginLeft: true, isYou: item.isActive, highlightColor: mergedOptions.highlightColor }, mergedOptions.highlightText)))));
    };
    return (React__default.createElement(PieChartWrapper, { horizontalLegend: mergedOptions.horizontalLegend },
        React__default.createElement("svg", { viewBox: '0 0 100 100', width: mergedOptions.width, height: mergedOptions.height, onMouseLeave: function () { return setIsHover(false); } },
            React__default.createElement(GroupCircle, { style: { display: 'block' } }, isHaveOneValue ? (React__default.createElement(OneCircle, { r: 48, cx: '50', cy: '50', fill: onlyValue.color, onMouseEnter: function () {
                    setCurrentHoverIndex(onlyValueIndex);
                    setIsHover(true);
                }, style: {
                    animationDuration: '.4s'
                } })) : (segments)),
            mergedOptions.isDonutChart && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("circle", { r: 28, cx: '50', cy: '50', fill: '#fff', style: { pointerEvents: 'none' } }),
                React__default.createElement("circle", { r: 25, cx: '50', cy: '50', fill: '#fff', stroke: 'rgba(69, 68, 89, 0.05)', strokeWidth: 2, style: { pointerEvents: 'none' } }),
                renderCenterText()))),
        renderHover(),
        mergedOptions.isShowLegend && (React__default.createElement(LineDetail, { onMouseLeave: function () {
                if (!mergedOptions.isFocusHoverOnBottomLegend) {
                    return;
                }
                setIsHover(false);
            } }, values.map(function (item, index) {
            var _a, _b;
            var isLegendHover = currentHoverIndex === index;
            if (!isHover) {
                isLegendHover = false;
            }
            // If option isFocusHover (is focus on hover) not enable
            // then set legend hover as default
            if (!mergedOptions.isFocusHover) {
                isLegendHover = true;
            }
            // Do not fade down legend if isFocusHoverOnBottomLegend is not enable
            if (!mergedOptions.isFocusHoverOnBottomLegend) {
                isLegendHover = true;
            }
            return (React__default.createElement(Legend, { key: "key-index-".concat(index), isHover: isHover, isLegendHover: isLegendHover, isCurrent: (_a = item.isActive) !== null && _a !== void 0 ? _a : false, highlightColor: mergedOptions.highlightColor, onMouseEnter: function () {
                    if (!mergedOptions.isFocusHoverOnBottomLegend) {
                        return;
                    }
                    setCurrentHoverIndex(index);
                    setIsHover(true);
                } },
                React__default.createElement(LegendLabel, { color: (_b = item.color) !== null && _b !== void 0 ? _b : DEFAULT_COLORS[0] },
                    item.label,
                    ' ',
                    mergedOptions.showHighlightTextOnLegend && item.isActive ? (React__default.createElement(TooltipText, { isMarginLeft: true, isYou: item.isActive, highlightColor: mergedOptions.highlightColor }, mergedOptions.highlightText)) : null),
                React__default.createElement(PercentView, null,
                    item.percent,
                    "%")));
        })))));
};

export { PieChart as default };
//# sourceMappingURL=index.js.map
